import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const done = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (done.current) return;
    done.current = true;
    (async () => {
      const hash = window.location.hash || '';
      const m = hash.match(/session_id=([^&]+)/);
      if (!m) {
        navigate('/login', { replace: true });
        return;
      }
      const session_id = decodeURIComponent(m[1]);
      // CRITICAL: Clear the hash *before* the network call so a page-refresh
      // during the exchange can't replay the same (now-consumed) session_id
      // against Emergent and get a 404.
      window.history.replaceState({}, '', window.location.pathname || '/');
      try {
        const r = await api.post('/auth/session', { session_id });
        setUser(r.data.user);
        navigate('/dashboard', { replace: true, state: { user: r.data.user } });
      } catch (e) {
        const msg = e?.response?.data?.detail || 'Sign in failed. Please try again.';
        setError(msg);
        toast.error(msg);
      }
    })();
  }, [navigate, setUser]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" data-testid="auth-callback-error">
        <div className="max-w-md w-full bg-white rounded-2xl border border-[#E8E5EC] p-8 text-center shadow-sm">
          <div className="w-12 h-12 rounded-full bg-[#8A3A3A]/15 flex items-center justify-center mx-auto mb-4">
            <AlertCircle size={22} className="text-[#8A3A3A]" />
          </div>
          <div className="font-serif text-xl text-[#2D2833] mb-2">Sign-in didn't complete</div>
          <div className="text-sm text-[#685B72] mb-6">{error}</div>
          <Button
            data-testid="auth-callback-retry"
            onClick={() => navigate('/login', { replace: true })}
            className="w-full h-11 bg-[#685B72] hover:bg-[#52475A] text-white rounded-xl text-sm font-semibold"
          >
            Back to sign in
          </Button>
          <div className="text-xs text-[#685B72]/80 mt-4">
            Trouble persists? Contact <span className="font-semibold">Davinder Sekhon</span>.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center text-[#685B72]" data-testid="auth-callback">
      <div className="flex flex-col items-center gap-3">
        <div className="animate-pulse font-serif text-lg">Completing sign-in…</div>
        <div className="text-xs">Just a moment.</div>
      </div>
    </div>
  );
}
